import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import './MessageActionButtons.css';
import { canExportToGSheets, canRegenerate, canAskAssistant, canApprove } from '../utils/permissions';

/**
 * MessageActionButtons component displays action buttons for message interactions
 *
 * @param {Object} props Component props
 * @param {Object} props.message The message object
 * @param {Function} props.toggleReference Function to toggle reference status
 * @param {Function} props.handleJsonDownload Function to download JSON
 * @param {Function} props.handleOpenJsonInNewTab Function to open JSON in new tab
 * @param {Function} props.handleExportToSheets Function to export to Google Sheets
 * @param {Function} props.handleApproveResult Function to approve result
 * @param {Function} props.handleRejectResult Function to reject result
 * @param {Function} props.handleAskAssistantClick Function to ask assistant
 * @param {Function} props.toggleTableCollapse Function to toggle table collapse
 * @param {boolean} props.isReferenced Whether the message is referenced
 * @param {boolean} props.isExporting Whether the message is being exported
 * @param {Object} props.user The user object containing permissions
 * @param {boolean} props.isTableCollapsed Whether the table is collapsed
 */
const MessageActionButtons = ({
  message,
  toggleReference,
  isReferenced,
  handleJsonDownload,
  handleOpenJsonInNewTab,
  handleExportToSheets,
  isExporting,
  user,
  toggleTableCollapse,
  isTableCollapsed,
  handleApproveResult,
  handleRejectResult,
  handleAskAssistantClick,
  handleEditCampaign,
  handleEditCreative,
  handleEditLaunchConfiguration,
  handleGetOriginalMessage
}) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerPosition, setDrawerPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef(null);
  const drawerRef = useRef(null);

  // Close drawer when clicking outside, scrolling, or resizing
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isDrawerOpen && buttonRef.current && !buttonRef.current.contains(event.target)) {
        // Check if the click is on a drawer item
        const drawerItems = document.querySelectorAll('.drawer-item');
        let isClickOnDrawerItem = false;

        drawerItems.forEach(item => {
          if (item.contains(event.target)) {
            isClickOnDrawerItem = true;
          }
        });

        if (!isClickOnDrawerItem) {
          setIsDrawerOpen(false);
        }
      }
    };

    // Close drawer on scroll or resize
    const handleScrollOrResize = () => {
      if (isDrawerOpen) {
        setIsDrawerOpen(false);
      }
    };

    // Find all scrollable parent elements and add scroll listeners
    const addScrollListenersToParents = () => {
      if (!buttonRef.current) return [];

      const scrollableParents = [];
      let parent = buttonRef.current.parentElement;

      while (parent) {
        const style = window.getComputedStyle(parent);
        const overflow = style.getPropertyValue('overflow');
        const overflowY = style.getPropertyValue('overflow-y');

        if (
          overflow === 'auto' ||
          overflow === 'scroll' ||
          overflowY === 'auto' ||
          overflowY === 'scroll'
        ) {
          scrollableParents.push(parent);
          parent.addEventListener('scroll', handleScrollOrResize, true);
        }

        parent = parent.parentElement;
      }

      return scrollableParents;
    };

    // Add listeners to common scrollable elements
    const messagesContainer = document.querySelector('.messages-container');
    if (messagesContainer) {
      messagesContainer.addEventListener('scroll', handleScrollOrResize, true);
    }

    // Use wheel event to detect scrolling with mouse wheel
    const handleWheel = () => {
      if (isDrawerOpen) {
        setIsDrawerOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScrollOrResize, true);
    document.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);
    document.addEventListener('wheel', handleWheel, true);

    // Add listeners to scrollable parents
    const scrollableParents = addScrollListenersToParents();

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScrollOrResize, true);
      document.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
      document.removeEventListener('wheel', handleWheel, true);

      // Remove listener from messages container
      if (messagesContainer) {
        messagesContainer.removeEventListener('scroll', handleScrollOrResize, true);
      }

      // Remove listeners from scrollable parents
      scrollableParents.forEach(parent => {
        parent.removeEventListener('scroll', handleScrollOrResize, true);
      });
    };
  }, [isDrawerOpen]);

  // Effect to measure and adjust drawer position after it's rendered
  useEffect(() => {
    if (isDrawerOpen && drawerRef.current) {
      const drawerHeight = drawerRef.current.offsetHeight;
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const spacing = 2; // Reduced spacing in pixels

      // Recalculate vertical position based on actual drawer height
      const spaceBelow = window.innerHeight - buttonRect.bottom;
      const spaceAbove = buttonRect.top;

      let newTop = drawerPosition.top;

      // If drawer doesn't fit below and there's more space above, position it above
      if (spaceBelow < drawerHeight && spaceAbove > spaceBelow) {
        newTop = buttonRect.top + window.scrollY - drawerHeight - spacing;
      }

      // Update position if needed
      if (newTop !== drawerPosition.top) {
        setDrawerPosition(prev => ({ ...prev, top: newTop }));
      }
    }
  }, [isDrawerOpen, drawerPosition.top]);

  if (!message || message.type !== 'bot' || !message.jsonResponse || message.isProcessing || message.isError || message.status === 'failed') {
    return null;
  }

  const toggleDrawer = (e) => {
    e.stopPropagation();

    if (!isDrawerOpen) {
      const buttonRect = e.currentTarget.getBoundingClientRect();
      const drawerWidth = 220; // Width of the drawer in pixels
      const drawerHeight = 300; // Approximate height of the drawer in pixels
      const spacing = 2; // Reduced spacing in pixels

      // Calculate available space in different directions
      const spaceBelow = window.innerHeight - buttonRect.bottom;
      const spaceAbove = buttonRect.top;
      const spaceRight = window.innerWidth - buttonRect.left;
      const spaceLeft = buttonRect.right;

      // Determine vertical position
      let top;
      if (spaceBelow >= drawerHeight || spaceBelow >= spaceAbove) {
        // Position below the button with minimal spacing
        top = buttonRect.bottom + window.scrollY + spacing;
      } else {
        // Position above the button with minimal spacing
        top = buttonRect.top + window.scrollY - drawerHeight - spacing;
      }

      // Determine horizontal position
      let left;
      if (spaceRight >= drawerWidth || spaceRight >= spaceLeft) {
        // Position to the right of the button
        left = buttonRect.left + window.scrollX;
      } else {
        // Position to the left of the button, aligned with right edge
        left = buttonRect.right + window.scrollX - drawerWidth;
      }

      setDrawerPosition({ top, left });
    }

    setIsDrawerOpen(!isDrawerOpen);
  };

  // Create a portal for the drawer
  const renderDrawer = () => {
    if (!isDrawerOpen) return null;

    // Create a portal to render the drawer at the root level
    return ReactDOM.createPortal(
      <div
        ref={drawerRef}
        className="message-actions-drawer"
        style={{
          position: 'fixed',
          top: `${drawerPosition.top}px`,
          left: `${drawerPosition.left}px`,
          zIndex: 99999
        }}
        onClick={(e) => e.stopPropagation()} // Prevent clicks from bubbling
      >
        <div className={`drawer-item ${isReferenced ? 'active' : ''}`}
          onClick={() => {
            toggleReference(message.id);
            setIsDrawerOpen(false);
          }}
        >
          <span className="material-symbols-outlined">link</span>
          <span>{isReferenced ? "Remove reference" : "Reference in chat"}</span>
        </div>

        <div className="drawer-item"
          onClick={() => {
            handleJsonDownload();
            setIsDrawerOpen(false);
          }}
        >
          <span className="material-symbols-outlined">download</span>
          <span>Download JSON</span>
        </div>

        <div className="drawer-item"
          onClick={() => {
            handleOpenJsonInNewTab();
            setIsDrawerOpen(false);
          }}
        >
          <span className="material-symbols-outlined">open_in_new</span>
          <span>Open JSON</span>
        </div>

        <div className={`drawer-item ${isExporting ? 'loading' : ''} ${isExporting || !canExportToGSheets(user) ? 'disabled' : ''}`}
          onClick={() => {
            if (!isExporting && canExportToGSheets(user)) {
              handleExportToSheets(message.id, message.jsonResponse);
              setIsDrawerOpen(true);
            }
          }}
        >
          <span className="material-symbols-outlined">
            {isExporting ? 'sync' : 'table_view'}
          </span>
          <span>Export to Google Sheets</span>
        </div>

        <div className={`drawer-item approve ${message.status === 'approved' ? 'active' : ''} ${!canApprove(user) ? 'disabled' : ''}`}
          onClick={() => {
            if (canApprove(user)) {
              handleApproveResult();
              setIsDrawerOpen(false);
            }
          }}
        >
          <span className="material-symbols-outlined">check</span>
          <span>{message.status === 'approved' ? 'Approved' : 'Approve'}</span>
        </div>

        {/* Campaign Launcher - Reject button */}
        {(message.action === 'campaign-launcher') && (
          <div className={`drawer-item reject ${message.status === 'rejected' ? 'active' : ''} ${!canRegenerate(user) ? 'disabled' : ''}`}
            onClick={() => {
              if (canRegenerate(user)) {
                handleRejectResult();
                setIsDrawerOpen(false);
              }
            }}
          >
            <span className="material-symbols-outlined">close</span>
            <span>{message.status === 'rejected' ? 'Rejected' : 'Reject'}</span>
          </div>
        )}

        {/* Edit button for campaign, creative, and campaign-launcher */}
        {(message.action === 'campaign-processor' || message.action === 'campaign-augmentor'
        || message.action === 'creative-processor' || message.action === 'creative-augmentor'
        ||  message.action === 'campaign-launcher' || message.action === 'launch-augmentor') && (
          <div className={`drawer-item edit ${!canRegenerate(user) ? 'disabled' : ''}`}
            onClick={() => {
              if (canRegenerate(user)) {
                // Find the original message that generated this response
                const originalMessage = handleGetOriginalMessage(message.id, message.sourceMessageId);

                // Pre-populate with validation summary if available
                let initialFeedback = '';

                // First check for validation summary
                if (message.jsonResponse?.validation?.summary) {
                  const summary = message.jsonResponse.validation.summary;

                  // Format the validation summary in a user-friendly way
                  initialFeedback = "Please fix the following issues:\n\n";

                  // Add overall status
                  if (summary.overall_status) {
                    initialFeedback += `Overall status: ${summary.overall_status}\n\n`;
                  }

                  // Add validation issues as a list
                  if (Array.isArray(summary.validation_issues) && summary.validation_issues.length > 0) {
                    initialFeedback += summary.validation_issues.map(issue => `- ${issue}`).join("\n");
                  } else if (typeof summary.validation_issues === 'string') {
                    initialFeedback += `- ${summary.validation_issues}`;
                  }

                  // Add counts if available
                  if (summary.fields_with_issues !== undefined && summary.total_fields_validated !== undefined) {
                    initialFeedback += `\n\n${summary.fields_with_issues} issues found out of ${summary.total_fields_validated} fields validated.`;
                  }
                } else if (message.jsonResponse?.pdf_processing_note) {
                  // Include PDF processing note if available
                  initialFeedback = `Note: ${message.jsonResponse.pdf_processing_note}\n\nPlease specify what changes you'd like to make:`;
                }

                // Choose the appropriate edit handler based on the message action
                if (message.action === 'creative-processor' || message.action === 'creative-augmentor') {
                  // Use the creative edit handler
                  handleEditCreative(message.id, message, initialFeedback, originalMessage);
                } else if (message.action === 'campaign-launcher' || message.action === 'launch-augmentor') {
                  // Use the launch configuration edit handler
                  handleEditLaunchConfiguration(message.id, message, initialFeedback, originalMessage);
                } else if (message.action === 'campaign-processor' || message.action === 'campaign-augmentor') {
                  // Default to campaign edit handler for other actions
                  handleEditCampaign(message.id, message, initialFeedback, originalMessage);
                }

                setIsDrawerOpen(false);
              }
            }}
          >
            <span className="material-symbols-outlined">edit</span>
            <span>Edit</span>
          </div>
        )}

        {/* Regenerate button for campaign and creative */}
        {(message.action === 'campaign-processor' || message.action === 'campaign-augmentor'
        || message.action === 'creative-processor' || message.action === 'creative-augmentor') && (
          <div className={`drawer-item regenerate ${!canRegenerate(user) ? 'disabled' : ''}`}
            onClick={() => {
              if (canRegenerate(user)) {
                // Simply resend the previous message
                handleRejectResult('');
                setIsDrawerOpen(false);
              }
            }}
          >
            <span className="material-symbols-outlined">refresh</span>
            <span>Regenerate</span>
          </div>
        )}

        {/* Ask Assistant button for campaign-launcher, launch-augmentor, fast-track, and fast-track-shell */}
        {(message.action === 'campaign-launcher' || message.action === 'launch-augmentor' || message.action === 'fast-track' || message.action === 'fast-track-shell') && (
          <div className={`drawer-item ${!canAskAssistant(user) ? 'disabled' : ''}`}
            onClick={() => {
              if (canAskAssistant(user)) {
                handleAskAssistantClick(message.id, message.sourceMessageId);
                setIsDrawerOpen(false);
              }
            }}
          >
            <span className="material-symbols-outlined">help</span>
            <span>Ask Assistant</span>
          </div>
        )}
      </div>,
      document.body
    );
  };

  return (
    <div className="message-action-buttons" aria-label="Message actions">
      {/* More actions button */}
      <button
        ref={buttonRef}
        onClick={toggleDrawer}
        className={`icon-button more-actions-btn ${isDrawerOpen ? 'active' : ''}`}
        title="More actions"
      >
        <span className="material-symbols-outlined">more_vert</span>
      </button>

      {/* Always visible expand/collapse button */}
      <button
        onClick={toggleTableCollapse}
        className="icon-button expand-json-btn"
        title={isTableCollapsed ? "Expand" : "Collapse"}
      >
        <span className="material-symbols-outlined">
          {isTableCollapsed ? 'expand_more' : 'expand_less'}
        </span>
      </button>

      {/* Render the drawer portal */}
      {renderDrawer()}
    </div>
  );
};

export default MessageActionButtons;
